import React, { useState } from 'react';
import { CloudOff, RefreshCw, UploadCloud } from 'lucide-react';
import { useOfflineState } from '../store';
import { OutboxSheet } from './OutboxSheet';

/**
 * The header surface: quiet when everything is sent, a plain count when it is
 * not. Being offline is normal for this team, so nothing here is red or loud.
 */
export const OfflineStatus: React.FC<{ size?: 'sm' | 'lg'; className?: string }> = ({
  size = 'sm',
  className = '',
}) => {
  const { online, waiting, blocked, syncing } = useOfflineState();
  const [open, setOpen] = useState(false);

  const nothingToSay = online && waiting === 0 && blocked === 0;
  if (nothingToSay) return null;

  const count = waiting + blocked;
  const label = !online
    ? count > 0
      ? `Offline — ${count} waiting to sync`
      : 'Offline — showing stored copies'
    : syncing
      ? 'Syncing'
      : `${count} waiting to sync`;

  const Icon = !online ? CloudOff : syncing ? RefreshCw : UploadCloud;
  const big = size === 'lg';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={`relative inline-flex flex-shrink-0 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          big ? 'h-11 w-11' : 'h-9 w-9'
        } ${className}`}
      >
        <Icon
          className={`${big ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'} ${syncing ? 'animate-spin' : ''}`}
          strokeWidth={1.75}
        />
        {count > 0 && (
          <span
            aria-hidden="true"
            className={`absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-medium leading-none text-white ${
              blocked > 0 ? 'bg-primary-600' : 'bg-amber-500'
            }`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <OutboxSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
};
